/// What this browser can actually play, asked of the browser itself.
///
/// The server decides on the basis of this profile alone, so guessing here would produce a
/// decision that looks right and a player that shows nothing. Everything below is probed with
/// canPlayType rather than assumed from the user agent.
/// The heights worth asking about, largest first, each with the codec level that goes with it.
///
/// The level matters as much as the size. Asking whether a browser decodes a 2160-line picture
/// while telling it the stream is High@4.0 - a level that cannot hold one - is asking a
/// question with no useful answer, and the answer comes back no for the wrong reason.
const sizes = [
  { height: 2160, h264: 'avc1.640033', hevc: 'hvc1.1.6.L153.B0' },
  { height: 1440, h264: 'avc1.640032', hevc: 'hvc1.1.6.L150.B0' },
  { height: 1080, h264: 'avc1.640028', hevc: 'hvc1.1.6.L120.B0' },
  { height: 720, h264: 'avc1.64001f', hevc: 'hvc1.1.6.L93.B0' },
  { height: 480, h264: 'avc1.64001e', hevc: 'hvc1.1.6.L90.B0' },
];

/// A realistic bitrate for a picture of this height, so the question is one worth asking. A
/// decoder that manages 4K at eight megabits and not at thirty is a decoder that stutters, and
/// finding that out is the whole point.
const bitrateFor = (height: number) => Math.round(height * height * 4);

/// The tallest picture this browser says it decodes smoothly in a codec, or undefined where it
/// will not say.
///
/// Asked of Media Capabilities rather than assumed, and asked about smoothness rather than mere
/// support: a phone will happily agree that it can decode 4K H.264 and then fail to, which
/// arrives as "Media failed to decode" over a black picture. Without this the server is told
/// nothing about size at all, and a 4K film is transcoded to 4K for a telephone.
async function tallestSmooth(
  contentTypeFor: (size: (typeof sizes)[number]) => string | undefined,
): Promise<number | undefined> {
  if (typeof navigator === 'undefined' || navigator.mediaCapabilities === undefined) {
    return undefined;
  }

  for (const size of sizes) {
    const contentType = contentTypeFor(size);

    if (contentType === undefined) {
      continue;
    }

    try {
      const info = await navigator.mediaCapabilities.decodingInfo({
        type: 'media-source',
        video: {
          contentType,
          width: Math.round((size.height * 16) / 9),
          height: size.height,
          bitrate: bitrateFor(size.height),
          framerate: 24,
        },
      });

      if (info.supported && info.smooth) {
        return size.height;
      }
    } catch {
      // A browser that will not answer for this shape is one nothing can be learnt from here.
      // The next size down is asked about rather than the whole codec being written off.
    }
  }

  return undefined;
}

/// Whether this screen can actually show a high dynamic range picture.
///
/// Two questions, asked in that order. "video-dynamic-range" is the one meant for exactly this -
/// it says whether video can be presented with high peak brightness and contrast - and
/// "dynamic-range" is the older, broader one that some browsers still only have. Neither means
/// the window is in HDR mode at this instant; both mean the display is one that can be.
///
/// Where neither answers, the honest reply is no. A screen wrongly called HDR is handed a
/// picture graded for a thousand nits and shows it washed out and dark - which is worse than
/// the server mapping it down, because the server maps it the same way every time.
function showsHighDynamicRange(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  for (const query of ['(video-dynamic-range: high)', '(dynamic-range: high)']) {
    try {
      const answer = window.matchMedia(query);

      // A query the browser does not know is reported back as "not all" and matches nothing.
      // Only that tells the two apart: comparing against the text that was asked would fail on
      // any browser that serialises the query differently from how it was written, and then an
      // HDR screen would be called SDR for the sake of a space.
      if (answer.media !== 'not all' && answer.matches) {
        return true;
      }
    } catch {
      // An older browser that throws on an unknown query. The next one is tried.
    }
  }

  return false;
}

/// How far behind the edge of a channel this player keeps, and so how much of it the server
/// waits for before handing it over. hls.js starts wherever it is told; AVPlayer's rule of three
/// target durations - six seconds at the least - is the server's to apply to players that need
/// it, not to this one. Three seconds of one-second segments still rides out a stutter between
/// the satellite and the encoder, and the channel is on screen about four seconds sooner.
export const kLiveHoldSeconds = 3;

export async function buildClientProfile(deviceId: string) {
  const probe = document.createElement('video');

  /// Everything this player receives goes through Media Source Extensions - that is what hls.js
  /// feeds and what a segmented stream is played by. So that is what gets asked.
  ///
  /// It is not the same question as canPlayType, and Safari is where the difference bites:
  /// canPlayType says yes to HEVC because Safari plays HEVC files, while its MediaSource will
  /// not take it. Asking the wrong one of the two gets a profile saying "send me HEVC", a
  /// server that obligingly encodes some, and a player that reports that the media failed to
  /// decode - which is exactly what it did.
  const mediaSource: { isTypeSupported(type: string): boolean } | undefined =
    typeof window === 'undefined'
      ? undefined
      : ((window as unknown as { ManagedMediaSource?: { isTypeSupported(type: string): boolean } })
          .ManagedMediaSource
        ?? (typeof MediaSource === 'undefined' ? undefined : MediaSource));

  /// "maybe" is not a capability. Chrome answers "maybe" for Matroska and then decodes
  /// nothing, which produces a black player and a server convinced it did the right thing.
  /// A container counts as supported only when the browser says "probably" for it together
  /// with a codec it would really contain.
  ///
  /// Where there is a MediaSource to ask, it is asked instead, and it answers yes or no.
  const definitely = (type: string) =>
    mediaSource !== undefined ? mediaSource.isTypeSupported(type) : probe.canPlayType(type) === 'probably';

  /// For a codec inside a container the browser has already confirmed, "maybe" is good enough:
  /// the uncertainty there is about profiles and levels, not about the format itself. A
  /// MediaSource, where there is one, is asked the same way - it is the thing that will have
  /// to accept the bytes.
  const supports = (type: string) =>
    mediaSource !== undefined ? mediaSource.isTypeSupported(type) : probe.canPlayType(type) !== '';

  const containers = [
    ['mp4', 'video/mp4; codecs="avc1.640028,mp4a.40.2"'],
    ['webm', 'video/webm; codecs="vp9,opus"'],
    ['matroska', 'video/x-matroska; codecs="avc1.640028,mp4a.40.2"'],
    ['mpegts', 'video/mp2t; codecs="avc1.640028,mp4a.40.2"'],
  ]
    .filter(([, mime]) => definitely(mime))
    .map(([container]) => container);

  /// A screen that can show it, and a decoder that can produce it, are two separate questions -
  /// and HDR needs both. The screen is asked once; the decoder is asked per codec below, with
  /// the ten bit form of each.
  const screenShowsHdr = showsHighDynamicRange();

  const videoCandidates = [
    {
      codec: 'h264',
      profiles: ['High', 'Main', 'Baseline'],
      type: 'video/mp4; codecs="avc1.640028"',
      at: (size: (typeof sizes)[number]) => `video/mp4; codecs="${size.h264}"`,

      // High 10 exists in the specification and in no browser. Nothing is gained by asking.
      deep: undefined,
      deepProfile: undefined,
    },
    {
      codec: 'hevc',
      profiles: ['Main', 'Main 10'],
      type: 'video/mp4; codecs="hvc1.1.6.L93.B0"',
      at: (size: (typeof sizes)[number]) => `video/mp4; codecs="${size.hevc}"`,

      // Main 10, level 5.1: what an HDR10 release is, and what Safari and Chrome on hardware
      // that can decode it answer yes to.
      deep: 'video/mp4; codecs="hvc1.2.4.L153.B0"',

      // "Main 10" is already in the list above.
      deepProfile: undefined,
    },
    {
      codec: 'vp9',
      profiles: ['Profile 0'],
      type: 'video/webm; codecs="vp9"',
      at: () => 'video/webm; codecs="vp09.00.10.08"',

      // Profile 2 is where VP9 keeps its ten bit - a profile of its own, so it has to be
      // offered alongside, or an encoder producing it is refused for a profile nobody claimed.
      deep: 'video/webm; codecs="vp09.02.10.10"',
      deepProfile: 'Profile 2',
    },
    {
      codec: 'av1',
      profiles: ['Main'],
      type: 'video/mp4; codecs="av01.0.05M.08"',
      at: () => 'video/mp4; codecs="av01.0.08M.08"',

      // AV1 keeps ten bit inside Main, so the profile list above already covers it.
      deep: 'video/mp4; codecs="av01.0.08M.10"',

      // AV1 keeps ten bit inside Main, which is already in the list above.
      deepProfile: undefined,
    },
  ]
    .filter((candidate) => supports(candidate.type));

  // What each one can be decoded at, measured. A codec the browser will say nothing about is
  // reported without a ceiling, which is what every codec was before this: the server then
  // sends the source resolution, and a client that cannot keep up says so by failing to decode.
  const videoCodecs = await Promise.all(
    videoCandidates.map(async ({ codec, profiles, at, deep, deepProfile }) => {
      const tallest = await tallestSmooth(at);

      /// Ten bit is claimed only where this browser says it decodes the ten bit form of this
      /// codec. Saying it without asking is how a profile ends up describing a screen rather
      /// than a decoder.
      const decodesDeep = deep !== undefined && supports(deep);

      /// And the range only where the screen can show it too. Both, or neither: a decoder that
      /// can read HDR into an SDR panel produces a dark, flat picture, and the server mapping
      /// it down is the better answer - which is what saying nothing here asks for.
      /// HDR10+ is HDR10 with metadata that changes scene by scene, carried where a decoder
      /// that does not read it simply ignores it - so a screen that shows HDR10 shows an HDR10+
      /// stream correctly, as its base layer. Leaving it out would map a picture down that this
      /// screen can show. Dolby Vision is not here: it is not compatible in that way.
      const ranges = screenShowsHdr && decodesDeep
        ? ['Sdr', 'Hdr10', 'Hdr10Plus', 'HybridLogGamma']
        : ['Sdr'];

      return {
        codec,
        profiles: decodesDeep && deepProfile !== undefined ? [...profiles, deepProfile] : profiles,
        maxBitDepth: decodesDeep ? 10 : 8,
        supportedRanges: ranges,
        ...(tallest === undefined ? {} : { maxHeight: tallest, maxWidth: Math.round((tallest * 16) / 9) }),
      };
    }),
  );

  /// Two channels, and a browser that mixes anything wider down to them by itself - which is
  /// what the audio graph does with every track it renders. Saying so is what keeps a
  /// six-channel soundtrack from being re-encoded for a pair of laptop speakers.
  ///
  /// No MP3, although every browser plays it. Anything remuxed or transcoded arrives as
  /// fragmented MP4 through hls.js, and hls.js names the audio in an MP4 only for AAC: MP3
  /// in one comes out as a bare "mp4a", which Chrome refuses outright - "this title cannot be
  /// played" over an old DivX episode. Re-encoding the sound to AAC is cheap; a file played
  /// as it is that carries MP3 is rare, since the only containers a browser plays whole are
  /// MP4 and WebM.
  const audioCodecs = [
    { codec: 'aac', maxChannels: 2, type: 'audio/mp4; codecs="mp4a.40.2"' },
    { codec: 'opus', maxChannels: 2, type: 'audio/webm; codecs="opus"' },
    { codec: 'vorbis', maxChannels: 2, type: 'audio/webm; codecs="vorbis"' },
    { codec: 'flac', maxChannels: 2, type: 'audio/flac' },
  ]
    .filter((candidate) => supports(candidate.type))
    .map(({ codec, maxChannels }) => ({ codec, maxChannels, canDownmix: true }));

  return {
    deviceId,
    clientName: 'Emiro Web',
    clientVersion: '1.0.0',
    containers,
    videoCodecs,
    audioCodecs,
    subtitles: [{ codec: 'webvtt', supportsEmbedded: false, supportsExternal: true }],
    // A browser deinterlaces nothing; saying otherwise would hand it interlaced frames.
    canDeinterlaceInHardware: false,
    // Browsers map HDR onto an SDR screen each in their own way, and some not at all; the
    // server's GPU does it the same way for every one of them.
    canToneMapInHardware: false,
    preferredRemuxContainers: ['mp4'],
    supportsSegmentedDelivery: false,

    // No ladder. Several versions of one picture means the server encodes every one of them,
    // and a person watches one: three encodes of a 4K film on the hardware of whoever is
    // hosting it, so that a rung can be changed which on a home network never is.
    //
    // What it was for - a connection that varies - is answered instead by this profile saying
    // honestly what this browser decodes, and by the quality somebody chooses for themselves
    // when they are away from home. Both are one encode.
    supportsAdaptiveBitrate: false,

    liveStartSeconds: kLiveHoldSeconds,
  };
}

/// A stable id for this browser, so sessions from one device are recognisable as one device.
export function browserDeviceId(): string {
  const key = 'emiro.deviceId';

  try {
    const existing = localStorage.getItem(key);

    if (existing) {
      return existing;
    }

    const created = crypto.randomUUID();
    localStorage.setItem(key, created);

    return created;
  } catch {
    return 'emiro-web';
  }
}
